import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { OrganizationProvider, useOrganization } from "@/hooks/useOrganization";
import { PublicOrgProvider, usePublicOrgContext } from "@/contexts/PublicOrgContext";
import { useUserPrimaryDomain } from "@/hooks/useUserPrimaryDomain";
import UnknownHostname from "@/components/UnknownHostname";
import { ViewModeProvider } from "@/hooks/useViewMode";
import ErrorBoundary from "@/components/ErrorBoundary";
import DevModeToggle from "@/components/DevModeToggle";
import { useUserRole } from "@/hooks/useUserRole";
import { useAppMode, AppModeProvider } from "@/hooks/useAppMode";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
// UPPDRAG-32: pure helper extracted from FeatureRoute for vitest coverage.
import { decideFeatureGate } from "@/lib/featureGateHelpers";
import { toast } from "sonner";
// UPPDRAG-19 PR-A: global auth-required gate. Provider mounts the
// modal once; consumers (PR-B + PR-C) call useAuthRequired().requireAuth()
// to trigger it from anywhere in the tree.
import { AuthRequiredProvider } from "@/contexts/AuthRequiredContext";
import AppLayout from "@/components/AppLayout";
import OrgThemeInjector from "@/components/OrgThemeInjector";
import { usePublicFavicon } from "@/hooks/usePublicFavicon";
import { usePublicTitle } from "@/hooks/usePublicTitle";
import ManagerLayout from "@/components/ManagerLayout";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthConfirm from "./pages/AuthConfirm";
import ResetPassword from "./pages/ResetPassword";
import PublicLandingPage from "./pages/public/PublicLandingPage";
import Book from "./pages/Book";
// UPPDRAG-19 PR-B: read-only schedule for anonymous visitors. RootRoute
// renders this when unauthenticated; /book also falls through to it.
import AnonymousBook from "./pages/AnonymousBook";
// SPÅR 1a: V1-baserade anon-aktivitets-pages mot public_activities_view.
// /aktiviteter och /aktiviteter/:id ersätter UPPDRAG-19 PR-C:s
// AnonymousOpenMatches+AnonymousOpenMatchDetail (V2, raderade i 1b).
import AnonymousActivities from "./pages/AnonymousActivities";
import AnonymousActivityDetail from "./pages/AnonymousActivityDetail";
import SkapaAktivitet from "./pages/player/SkapaAktivitet";
import CoachDashboard from "./pages/coach/CoachDashboard";
import CoachGroupDetail from "./pages/coach/CoachGroupDetail";
import CoachAvailability from "./pages/coach/CoachAvailability";
import Play from "./pages/Play";
import Bookings from "./pages/Bookings";
import Profile from "./pages/Profile";
import Notifications from "./pages/Notifications";
import Leagues from "./pages/Leagues";
import LeagueDetail from "./pages/LeagueDetail";
import LeaguesMine from "./pages/LeaguesMine";
import Training from "./pages/Training";
import Tournaments from "./pages/Tournaments";
import TournamentDetail from "./pages/TournamentDetail";
import Players from "./pages/Players";
import PlayerProfile from "./pages/PlayerProfile";
import Messages from "./pages/Messages";
import DirectMessage from "./pages/DirectMessage";
import TVDisplay from "./pages/TVDisplay";
import Community from "./pages/Community";
import ActivityDetail from "./pages/ActivityDetail";
import ViewSelector from "./pages/ViewSelector";
import ManagerDashboard from "./pages/ManagerDashboard";
import DashboardBookingsToday from "./pages/admin/DashboardBookingsToday";
import DashboardRevenue from "./pages/admin/DashboardRevenue";
import DashboardUnpaid from "./pages/admin/DashboardUnpaid";
import DashboardOccupancy from "./pages/admin/DashboardOccupancy";
import CustomerDetailPage from "./pages/admin/CustomerDetailPage";
// UPPDRAG-30: DomainsPage no longer rendered as a top-level route — it's
// now consumed by AdminSettings via the "domains" tab. The /manager/domains
// path is kept as a redirect (see Routes block below) so bookmarks
// continue to work.
import JoinTeam from "./pages/JoinTeam";
import ActivityInvite from "./pages/ActivityInvite";

// Admin components used as full pages in manager view
import AdminScheduleGrid from "@/components/admin/AdminScheduleGrid";
import AdminCustomers from "@/components/admin/AdminCustomers";
import AdminCustomerGroups from "@/components/admin/AdminCustomerGroups";
import AdminCourts from "@/components/admin/AdminCourts";
import AdminPricing from "@/components/admin/AdminPricing";
import AdminBookings from "@/components/admin/AdminBookings";
import AdminWaitlist from "@/components/admin/AdminWaitlist";
import AdminRecurring from "@/components/admin/AdminRecurring";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminLeagues from "@/components/admin/AdminLeagues";
// UPPDRAG-22: AdminTeams no longer mounted at /manager/teams; now lives
// as a tab inside AdminLeagues (viewMode="teams"). Standalone route +
// import removed.
import AdminTournaments from "@/components/admin/AdminTournaments";
import AdminTraining from "@/components/admin/AdminTraining";
import AdminMembership from "@/components/admin/AdminMembership";
import AdminInvoices from "@/components/admin/AdminInvoices";
import AdminBroadcast from "@/components/admin/AdminBroadcast";
import AdminStatistics from "@/components/admin/AdminStatistics";
import AdminSettings from "@/components/admin/AdminSettings";

import NotFound from "./pages/NotFound";
import Maintenance from "./pages/Maintenance";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  return <AppLayout>{children}</AppLayout>;
}

// UPPDRAG-19 PR-B: hybrid route for /book. Authenticated users get the
// full Book page (mutations, multi-select, waitlist, etc.).
// Unauthenticated users get the read-only AnonymousBook with
// AuthRequiredModal-gated clicks. No redirect-to-/auth.
function BookOrAnonymous() {
  const { loading, isAuthenticated } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAuthenticated) return <AnonymousBook />;
  return <AppLayout><Book /></AppLayout>;
}

// Spår 1b: OpenMatchesOrAnonymous + OpenMatchDetailOrAnonymous (UPPDRAG-19 PR-C
// hybrid-routes mot V2) rivna. V2-pages raderade. /play/open-matches/* routes
// redirectas till /aktiviteter (V1 anon-vy via Spår 1a).

// T2v2/G-099: legacy V2 detail links (e.g. from Messages) keep their id when
// redirected to the V1 anon detail route (which itself bounces authenticated
// users on to /activities/:id).
function LegacyOpenMatchRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/aktiviteter/${id}` : "/aktiviteter"} replace />;
}

// T2v2/G-073: /invite/:token must stay reachable for anonymous email
// recipients, but authenticated users should get the app shell (nav) instead
// of a bare page. Same hybrid pattern as BookOrAnonymous.
function InviteWithLayout() {
  const { loading, isAuthenticated } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAuthenticated) return <ActivityInvite />;
  return <AppLayout><ActivityInvite /></AppLayout>;
}

// UPPDRAG-32: improved FeatureRoute — accepts an optional redirectTo
// (so manager routes land back on /manager/dashboard instead of /),
// and shows a Swedish toast when redirecting so admins understand
// why they were bounced. The decision logic itself is in
// decideFeatureGate (testable via vitest).
function FeatureRoute({
  feature,
  children,
  redirectTo,
}: {
  feature: string;
  children: React.ReactNode;
  redirectTo?: string;
}) {
  const { isEnabled, isLoading } = useFeatureFlags();
  const decision = decideFeatureGate({
    isLoading,
    isEnabled: isEnabled(feature),
    redirectTo,
  });

  // Fire toast in an effect so we don't trigger render-phase side effects.
  // Keyed on feature so a different gate doesn't double-fire.
  useEffect(() => {
    if (decision.type === "redirect") {
      toast.error(decision.toastMessage);
    }
    // Intentionally NOT depending on `decision.toastMessage` directly —
    // we only want one toast per (feature, redirect-state) transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision.type, feature]);

  if (decision.type === "loading") return null;
  if (decision.type === "redirect") return <Navigate to={decision.to} replace />;
  return <>{children}</>;
}

function ManagerRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  const { organization, isLoading: orgLoading } = useOrganization();
  const { org: publicOrg, isLoading: publicOrgLoading } = usePublicOrgContext();
  const { data: primaryDomain, isLoading: primaryDomainLoading } =
    useUserPrimaryDomain(organization?.id ?? null);
  const { isLoading, isError, roles } = useUserRole();

  // F-3: Cross-hostname admin-redirect
  // Om admin landar på en hostname som tillhör annan org (t.ex. AP Nacka-admin
  // besöker demo.boply.app/manager), redirecta till egen primary domain.
  // Ref: audit-reports/feat-domain-mapping-audit/rapport.md F-3
  useEffect(() => {
    if (orgLoading || publicOrgLoading || primaryDomainLoading) return;
    if (!organization || !publicOrg) return;
    if (organization.id === publicOrg.id) return; // samma org → OK
    if (!primaryDomain) return; // ingen fallback → visa error (se nedan)

    // F-3a: Defensive loop-guard. Om primaryDomain råkar peka på samma
    // hostname vi redan är på (t.ex. stale data där samma hostname
    // oavsiktligt slått över till två orgs) — skippa redirect för att
    // undvika infinite loop. UNIQUE-constraint på hostname i
    // organization_domains förhindrar normalt detta, men guarden skyddar
    // mot framtida constraint-relaxation eller bug i pub-org-resolution.
    const currentHostname = window.location.hostname.toLowerCase();
    if (primaryDomain.toLowerCase() === currentHostname) {
      console.warn(
        "[ManagerRoute] primaryDomain equals current hostname but orgs differ — skipping redirect to avoid loop",
      );
      return;
    }

    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `https://${primaryDomain}${currentPath}`;
  }, [organization, publicOrg, primaryDomain, orgLoading, publicOrgLoading, primaryDomainLoading]);
  // Some Admin*-komponenter deklarerar en egen useQuery med samma
  // queryKey ["user-roles", user.id, org.id] men utan att mappa ned till
  // string-array — det polluerar React Query-cachen med {role}[] istället
  // för string[], vilket gör att useUserRole's roles.includes("admin") ger
  // false och fires en spurious Navigate("/"). Räkna om isAdmin lokalt och
  // acceptera båda cache-shapes tills duplikaten tas bort i en separat
  // sprint (se Issue 1.1 "Properly" i overnight-test-v2).
  const isAdmin = Array.isArray(roles) && roles.some((r: unknown) =>
    typeof r === "string"
      ? r === "admin"
      : !!r && typeof (r as { role?: unknown }).role === "string" && (r as { role: string }).role === "admin"
  );
  const queryClient = useQueryClient();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) { setTimedOut(false); return; }
    const t = setTimeout(() => setTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, [isLoading]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  // Wait for organization before evaluating admin role. useUserRole's query is
  // disabled until organization?.id is known, and during that window its
  // isLoading flag is false while roles default to [] — which would make
  // isAdmin=false below and fire a spurious Navigate("/"). Guarding here keeps
  // the fix local to ManagerRoute without touching the shared hooks.
  if (orgLoading || !organization) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (isLoading && !timedOut) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (isError || timedOut) {
    return (
      <ManagerLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <ShieldAlert className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold">Kunde inte verifiera behörighet</h2>
          <p className="text-sm text-muted-foreground mb-4">Rollkontrollen tog för lång tid. Försök igen.</p>
          <Button onClick={() => { setTimedOut(false); queryClient.invalidateQueries({ queryKey: ["user-roles"] }); }}>Försök igen</Button>
        </div>
      </ManagerLayout>
    );
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // F-3: Cross-hostname mismatch utan primary domain → förklarande error
  // (redirect ovan skulle ha körts om primaryDomain fanns)
  if (organization && publicOrg && organization.id !== publicOrg.id && !primaryDomain) {
    return (
      <ManagerLayout>
        <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
          <ShieldAlert className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">Fel klubb</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            Du är admin för <strong>{organization.name}</strong> men besöker{" "}
            <strong>{publicOrg.name}</strong>. Ingen primary domain konfigurerad
            för din klubb än — kontakta support.
          </p>
        </div>
      </ManagerLayout>
    );
  }

  return <ManagerLayout>{children}</ManagerLayout>;
}

function CoachRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  const { roles, isAdmin, isLoading } = useUserRole();
  if (loading || isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  const isCoach = roles.includes("coach");
  if (!isCoach && !isAdmin) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AdminRedirectRoute() {
  const { loading, isAuthenticated } = useAuth();
  const { isAdmin, isLoading } = useUserRole();
  if (loading || isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (isAdmin) return <Navigate to="/manager/dashboard" replace />;
  return <Navigate to="/" replace />;
}

function RootRoute() {
  const { loading, isAuthenticated } = useAuth();
  const { isAdmin, isLoading } = useUserRole();
  const { mode } = useAppMode();

  if (loading || isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  // UPPDRAG-19 PR-B (Q1): unauthenticated visitors see the read-only
  // schedule directly instead of being redirected to /auth. They can
  // browse + see prices + court sponsors; clicking an empty slot
  // triggers AuthRequiredModal (PR-A).
  if (!isAuthenticated) return <AnonymousBook />;

  // Admin with no mode selected → show selector
  if (isAdmin && !mode) {
    return <ViewSelector />;
  }

  // Admin who chose manager → redirect to manager
  if (isAdmin && mode === "manager") {
    return <Navigate to="/manager/dashboard" replace />;
  }

  // Player mode or non-admin
  return <AppLayout><Index /></AppLayout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// PR-AM: title + favicon are now driven by PublicOrgContext (hostname-based,
// resolved before auth) via hooks called inside HostnameGate. This component
// kept as a no-op stub for backward-compat with the existing render tree —
// safe to remove the JSX-mount in a follow-up PR.
function DynamicTitle(): null {
  return null;
}

// `title` is an i18n key in the admin namespace (e.g. "managerTitles.tournaments").
function ManagerPage({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useTranslation("admin");
  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">{t(title)}</h1>
      {children}
    </div>
  );
}

/**
 * HostnameGate — renderas INUTI BrowserRouter men utanför AuthProvider.
 * Om hostname-lookup pågår visas laddning; om okänd hostname visas
 * UnknownHostname-vy istället för hela routing-trädet.
 *
 * Ref: audit-reports/feat-domain-mapping/rapport.md
 */
function HostnameGate({ children }: { children: React.ReactNode }) {
  const { isLoading, isUnknownHostname } = usePublicOrgContext();

  // PR-AM: per-tenant favicon + tab title. Hooks must run BEFORE the
  // conditional returns below (Rules of Hooks). They handle their own
  // loading/unknown/resolved branches internally.
  usePublicFavicon();
  usePublicTitle();

  if (isLoading) {
    return <UnknownHostname isLoading />;
  }

  if (isUnknownHostname) {
    return <UnknownHostname />;
  }

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PublicOrgProvider>
         <HostnameGate>
        <AuthProvider>
          <OrganizationProvider>
            <AppModeProvider>
            <ViewModeProvider>
              {/* UPPDRAG-19 PR-A: AuthRequiredProvider wraps everything
                  inside the routing tree. The provider mounts a single
                  AuthRequiredModal as a child, which is invisible until
                  any descendant calls useAuthRequired().requireAuth(). */}
              <AuthRequiredProvider>
              <OrgThemeInjector />
              <DynamicTitle />
              <ErrorBoundary>
              <MaintenanceGate>
              <Routes>
                {/* Maintenance page — always reachable for verification */}
                <Route path="/maintenance" element={<Maintenance />} />
                {/* Public shareable link routes (no auth required) */}
                <Route path="/m/:code" element={<PublicLandingPage />} />
                <Route path="/a/:code" element={<PublicLandingPage />} />
                <Route path="/join-team/:token" element={<JoinTeam />} />
                {/* T2v2/G-073: hybrid — anonymous email recipients get the bare page,
                    authenticated users get the app shell. */}
                <Route path="/invite/:token" element={<InviteWithLayout />} />
                <Route path="/b/:code" element={<PublicLandingPage />} />
                <Route path="/t/:code" element={<PublicLandingPage />} />
                <Route path="/s/:code" element={<PublicLandingPage />} />
                <Route path="/e/:code" element={<PublicLandingPage />} />
                <Route path="/tr/:code" element={<PublicLandingPage />} />
                <Route path="/pl/:code" element={<PublicLandingPage />} />
                <Route path="/g/:code" element={<PublicLandingPage />} />
                <Route path="/join/:code" element={<PublicLandingPage />} />
                <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/" element={<RootRoute />} />
                {/* UPPDRAG-19 PR-B: /book is anon-accessible — falls
                    through to AnonymousBook when not logged in (read-
                    only schedule + AuthRequiredModal-gated clicks).
                    Authenticated users see the existing Book page. */}
                <Route path="/book" element={<BookOrAnonymous />} />
                <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                <Route path="/leagues" element={<ProtectedRoute><FeatureRoute feature="leagues"><Leagues /></FeatureRoute></ProtectedRoute>} />
                {/* MEGA-SPRINT Sprint 6 V-3 Spelarvy: "Mina matcher" route. Must come BEFORE /leagues/:id to avoid the param catch-all matching "mine". */}
                <Route path="/leagues/mine" element={<ProtectedRoute><FeatureRoute feature="leagues"><LeaguesMine /></FeatureRoute></ProtectedRoute>} />
                <Route path="/leagues/:id" element={<ProtectedRoute><FeatureRoute feature="leagues"><LeagueDetail /></FeatureRoute></ProtectedRoute>} />
                <Route path="/training" element={<ProtectedRoute><FeatureRoute feature="training"><Training /></FeatureRoute></ProtectedRoute>} />
                <Route path="/coach" element={<CoachRoute><CoachDashboard /></CoachRoute>} />
                <Route path="/coach/groups/:id" element={<CoachRoute><CoachGroupDetail /></CoachRoute>} />
                <Route path="/coach/availability" element={<CoachRoute><CoachAvailability /></CoachRoute>} />
                <Route path="/play" element={<ProtectedRoute><Play /></ProtectedRoute>} />
                {/* SPÅR 1a: V1-baserade anon-routes mot public_activities_view. */}
                <Route path="/aktiviteter" element={<AnonymousActivities />} />
                <Route path="/aktiviteter/:id" element={<AnonymousActivityDetail />} />
                {/* SPÅR 1b: V2-routes (UPPDRAG-19 PR-C, /play/open-matches*)
                    raderade. Klient-redirect till /aktiviteter för listsidor;
                    :id-route har ingen V1-motsvarighet eftersom V2-match-ids
                    inte längre existerar — redirecta till listan. P3-default. */}
                <Route path="/play/open-matches" element={<Navigate to="/aktiviteter" replace />} />
                <Route path="/play/open-matches/create" element={<Navigate to="/book" replace />} />
                {/* T2v2/G-099: preserve the id when redirecting legacy V2 detail links. */}
                <Route path="/play/open-matches/:id" element={<LegacyOpenMatchRedirect />} />
                <Route path="/activities/:id" element={<ProtectedRoute><ActivityDetail /></ProtectedRoute>} />
                {/* T2v2 route canon (decision 1a+rename, 2b): the wizard is canonical on
                    /create-activity; /skapa-aktivitet redirects (bookmarks + old refs).
                    /create-match → /book — matches are created in the booking flow (T1). */}
                <Route path="/create-match" element={<Navigate to="/book" replace />} />
                <Route path="/create-activity" element={<ProtectedRoute><SkapaAktivitet /></ProtectedRoute>} />
                <Route path="/skapa-aktivitet" element={<Navigate to="/create-activity" replace />} />
                <Route path="/open-matches" element={<Navigate to="/aktiviteter" replace />} />
                <Route path="/join" element={<PublicRoute><Auth /></PublicRoute>} />
                <Route path="/tournaments" element={<ProtectedRoute><FeatureRoute feature="tournaments"><Tournaments /></FeatureRoute></ProtectedRoute>} />
                <Route path="/tournaments/:id" element={<ProtectedRoute><FeatureRoute feature="tournaments"><TournamentDetail /></FeatureRoute></ProtectedRoute>} />
                {/* UPPDRAG-34: gate player discovery (/players list +
                    /players/:id profile) behind the `players` feature
                    flag. Default-on preserves pre-flag behaviour;
                    halls that disable it get the standard FeatureRoute
                    redirect-to-/ + Swedish toast. */}
                <Route path="/players" element={<ProtectedRoute><FeatureRoute feature="players"><Players /></FeatureRoute></ProtectedRoute>} />
                <Route path="/players/:id" element={<ProtectedRoute><FeatureRoute feature="players"><PlayerProfile /></FeatureRoute></ProtectedRoute>} />
                <Route path="/messages" element={<ProtectedRoute><FeatureRoute feature="chat"><Messages /></FeatureRoute></ProtectedRoute>} />
                <Route path="/messages/:id" element={<ProtectedRoute><FeatureRoute feature="chat"><DirectMessage /></FeatureRoute></ProtectedRoute>} />
                <Route path="/community" element={<ProtectedRoute><FeatureRoute feature="community"><Community /></FeatureRoute></ProtectedRoute>} />
                <Route path="/tv" element={<TVDisplay />} />
                {/* T2v2 (decision 5a): /onboarding removed — it let any signed-in user
                    create an org/hall/admin-role client-side (G-037). Falls to NotFound. */}

                {/* Legacy admin redirect */}
                <Route path="/admin" element={<AdminRedirectRoute />} />

                {/* Manager routes */}
                <Route path="/manager/dashboard" element={<ManagerRoute><ManagerDashboard /></ManagerRoute>} />
                <Route path="/manager/dashboard/bookings-today" element={<ManagerRoute><DashboardBookingsToday /></ManagerRoute>} />
                <Route path="/manager/dashboard/revenue" element={<ManagerRoute><DashboardRevenue /></ManagerRoute>} />
                <Route path="/manager/dashboard/unpaid" element={<ManagerRoute><DashboardUnpaid /></ManagerRoute>} />
                <Route path="/manager/dashboard/occupancy" element={<ManagerRoute><DashboardOccupancy /></ManagerRoute>} />
                <Route path="/manager/customers/:id" element={<ManagerRoute><CustomerDetailPage /></ManagerRoute>} />
                <Route path="/manager/schedule" element={<ManagerRoute><ManagerPage title="managerTitles.schedule"><AdminScheduleGrid /></ManagerPage></ManagerRoute>} />
                <Route path="/manager/customers" element={<ManagerRoute><ManagerPage title="managerTitles.customers"><AdminCustomers /></ManagerPage></ManagerRoute>} />
                <Route path="/manager/customer-groups" element={<ManagerRoute><ManagerPage title="managerTitles.customerGroups"><AdminCustomerGroups /></ManagerPage></ManagerRoute>} />
                <Route path="/manager/courts" element={<ManagerRoute><ManagerPage title="managerTitles.courts"><AdminCourts /></ManagerPage></ManagerRoute>} />
                <Route path="/manager/pricing" element={<ManagerRoute><ManagerPage title="managerTitles.pricing"><AdminPricing /></ManagerPage></ManagerRoute>} />
                <Route path="/manager/bookings" element={<ManagerRoute><ManagerPage title="managerTitles.bookings"><AdminBookings /></ManagerPage></ManagerRoute>} />
                {/* UPPDRAG-32: feature-gated manager routes. Sidebar already
                    hides these items when the flag is off, but direct URL
                    access bypasses the sidebar — without FeatureRoute,
                    admins of orgs with leagues=false could still load
                    /manager/leagues and break things. Redirect to
                    /manager/dashboard with a Swedish toast on disabled. */}
                <Route path="/manager/waitlist" element={<ManagerRoute><FeatureRoute feature="waitlist" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.waitlist"><AdminWaitlist /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/recurring" element={<ManagerRoute><FeatureRoute feature="recurring" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.recurring"><AdminRecurring /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/users" element={<ManagerRoute><ManagerPage title="managerTitles.users"><AdminUsers /></ManagerPage></ManagerRoute>} />
                <Route path="/manager/leagues" element={<ManagerRoute><FeatureRoute feature="leagues" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.leagues"><AdminLeagues /></ManagerPage></FeatureRoute></ManagerRoute>} />
                {/* UPPDRAG-22: /manager/teams removed; AdminTeams is now a tab inside AdminLeagues. */}
                <Route path="/manager/tournaments" element={<ManagerRoute><FeatureRoute feature="tournaments" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.tournaments"><AdminTournaments /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/training" element={<ManagerRoute><FeatureRoute feature="training" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.training"><AdminTraining /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/membership" element={<ManagerRoute><FeatureRoute feature="memberships" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.membership"><AdminMembership /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/invoices" element={<ManagerRoute><FeatureRoute feature="invoicing" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.invoicing"><AdminInvoices /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/broadcast" element={<ManagerRoute><FeatureRoute feature="community" redirectTo="/manager/dashboard"><ManagerPage title="managerTitles.messages"><AdminBroadcast /></ManagerPage></FeatureRoute></ManagerRoute>} />
                <Route path="/manager/reports" element={<ManagerRoute><ManagerPage title="managerTitles.reports"><AdminStatistics /></ManagerPage></ManagerRoute>} />
                {/* UPPDRAG-30: /manager/domains is now a tab inside
                    Inställningar (alongside Webhooks & API). Keep the old
                    URL alive as a redirect so existing bookmarks +
                    deep-links don't break. AdminSettings reads
                    `?tab=domains` and opens the Domäner section. */}
                <Route path="/manager/domains" element={<Navigate to="/manager/settings?tab=domains" replace />} />
                <Route path="/manager/settings" element={<ManagerRoute><ManagerPage title="managerTitles.settings"><AdminSettings /></ManagerPage></ManagerRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
              </MaintenanceGate>
              </ErrorBoundary>
              <DevModeToggle />
              </AuthRequiredProvider>
            </ViewModeProvider>
            </AppModeProvider>
          </OrganizationProvider>
        </AuthProvider>
         </HostnameGate>
        </PublicOrgProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
